import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { 
  GraduationCap, 
  Briefcase, 
  Users, 
  Building2, 
  Ship, 
  Warehouse, 
  Truck, 
  Package, 
  Pill,
  Mail,
  Phone,
  Globe,
  MapPin,
  ChevronRight,
  CheckCircle2,
  Target,
  Lightbulb,
  Handshake,
  Rocket,
  Menu,
  X,
  Quote,
  Star,
  Send,
  User,
  MessageSquare,
  Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_student-centric-web/artifacts/5y6m7tgl_WhatsApp%20Image%202026-05-27%20at%2012.39.25%20PM.jpeg";

// Navigation Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  const navLinks = [
    { href: "#home", label: "Home" },
    { href: "#about", label: "About Us" },
    { href: "#founder", label: "Founder" },
    { href: "#services", label: "What We Do" },
    { href: "#career-path", label: "Career Path" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/95 backdrop-blur-md border-b border-amber-500/20" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-36">
          <a href="#home" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="Elites Route Global Talent" className="h-32 w-auto" />
          </a>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-zinc-300 hover:text-amber-400 transition-colors text-sm font-medium tracking-wide"
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden text-zinc-300 hover:text-amber-400"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-amber-500/20" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block py-3 text-zinc-300 hover:text-amber-400 transition-colors"
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const Hero = () => (
  <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950"></div>
    <div className="absolute inset-0 opacity-30">
      <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-600/10 rounded-full blur-3xl"></div>
    </div>
    
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="text-center">
        
        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight" data-testid="hero-title">
          Your Gateway to a<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
            Global Logistics Career
          </span>
        </h1>
        
        <p className="text-lg sm:text-xl text-zinc-400 max-w-3xl mx-auto mb-10 leading-relaxed" data-testid="hero-description">
          Hey future logistics pros! Ready to kickstart your career in supply chain, freight forwarding, and global trade? 
          We connect talented students like you with top companies worldwide.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
          <a 
            href="#services" 
            className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-zinc-950 font-semibold rounded-full hover:shadow-lg hover:shadow-amber-500/30 transition-all transform hover:-translate-y-1"
            data-testid="explore-services-btn"
          >
            Explore Services
          </a>
          <a 
            href="#contact" 
            className="px-8 py-4 border-2 border-amber-500/50 text-amber-400 font-semibold rounded-full hover:bg-amber-500/10 transition-all"
            data-testid="contact-us-btn"
          >
            Get In Touch
          </a>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
          <div className="text-center" data-testid="stat-focused">
            <div className="text-3xl sm:text-4xl font-bold text-amber-400 mb-2">100%</div>
            <div className="text-zinc-500 text-sm">Logistics Focused</div>
          </div>
          <div className="text-center" data-testid="stat-placements">
            <div className="text-3xl sm:text-4xl font-bold text-amber-400 mb-2">500+</div>
            <div className="text-zinc-500 text-sm">Students Placed</div>
          </div>
          <div className="text-center" data-testid="stat-partners">
            <div className="text-3xl sm:text-4xl font-bold text-amber-400 mb-2">50+</div>
            <div className="text-zinc-500 text-sm">Partner Companies</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// About Section
const About = () => (
  <section id="about" className="py-24 bg-zinc-900" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="about-title">
          About <span className="text-amber-400">Elites Route Global Talent</span>
        </h2>
        <p className="text-zinc-400 max-w-3xl mx-auto text-lg">
          Your dedicated partner in logistics talent acquisition and workforce development. 
          We specialize in connecting fresh graduates with exciting careers in global trade.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
        <div className="relative">
          <img 
            src="https://images.unsplash.com/photo-1613690399151-65ea69478674?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxzaGlwcGluZyUyMGNvbnRhaW5lcnN8ZW58MHx8fHwxNzY2Njg2MDUyfDA&ixlib=rb-4.1.0&q=85"
            alt="Global Logistics"
            className="rounded-2xl shadow-2xl w-full"
            data-testid="about-image"
          />
        </div>
        
        <div>
          <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
            <Handshake className="text-amber-400" />
            Connecting Dreams to Opportunities
          </h3>
          <p className="text-zinc-400 mb-6 leading-relaxed">
            With a global vision, we're here to help you land your dream job in logistics! 
            Whether you're a fresh graduate or looking to level up your career, we've got your back.
          </p>
          <p className="text-zinc-400 mb-8 leading-relaxed">
            We focus on nurturing fresh talent like you, providing industry-relevant training, and connecting 
            you with companies that value your potential. Your success is our mission!
          </p>
          
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-zinc-800/50 p-6 rounded-xl border border-zinc-700" data-testid="vision-card">
              <Target className="text-amber-400 mb-3" size={32} />
              <h4 className="text-white font-semibold mb-2">Our Vision</h4>
              <p className="text-zinc-400 text-sm">
                To become your trusted global partner in building a successful logistics career that drives innovation and growth.
              </p>
            </div>
            <div className="bg-zinc-800/50 p-6 rounded-xl border border-zinc-700" data-testid="mission-card">
              <Lightbulb className="text-amber-400 mb-3" size={32} />
              <h4 className="text-white font-semibold mb-2">Our Mission</h4>
              <p className="text-zinc-400 text-sm">
                Empower students with skills, connect you with top employers, and support your journey to becoming logistics leaders.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Founder Story Section
const FounderStory = () => (
  <section id="founder" className="py-24 bg-zinc-950" data-testid="founder-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="founder-title">
          Founder <span className="text-amber-400">Story</span>
        </h2>
        <p className="text-zinc-400 max-w-3xl mx-auto text-lg">
          Meet the visionary behind Elites Route Global Talent
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <div className="relative">
          <img 
            src="https://customer-assets.emergentagent.com/job_student-centric-web/artifacts/86ygfctw_SAFIJO%20PHOTO.jfif"
            alt="Safijo Alphons - Founder"
            className="rounded-2xl shadow-2xl w-full"
            data-testid="founder-image"
          />
          <div className="absolute -bottom-4 -right-4 bg-amber-500 text-zinc-950 px-6 py-4 rounded-xl shadow-xl hidden sm:block">
            <div className="text-lg font-bold">Safijo Alphons</div>
            <div className="text-sm">Founder & Director</div>
          </div>
        </div>
        
        <div className="space-y-6">
          <div>
            <h3 className="text-2xl font-bold text-white mb-4">Safijo Alphons</h3>
            <p className="text-amber-400 font-medium mb-4">Founder & Director | Logistics Coach | Career Consultant</p>
          </div>
          
          <p className="text-zinc-400 leading-relaxed">
            Every successful professional journey begins with guidance, opportunity, and the right exposure to industry. 
            For many graduates, especially in emerging sectors like logistics and supply chain, that bridge between 
            education and employment is often missing. Recognizing this gap, Safijo Alphons set out on a mission to 
            help young graduates discover meaningful careers in the logistics industry.
          </p>
          
          <p className="text-zinc-400 leading-relaxed">
            His journey began with a deep interest in the logistics and supply chain field and a strong belief that 
            practical industry knowledge is the key to career success. Over the years, Safijo Alphons has actively 
            worked with students, colleges, and industry professionals to understand the evolving skill requirements 
            of the logistics sector.
          </p>
          
          <p className="text-zinc-400 leading-relaxed">
            Through training sessions, career guidance programs, and industry interactions, he has helped many graduates 
            build confidence and secure opportunities in logistics and warehouse operations. His work has taken him to 
            several colleges where he has served as a resource person for logistics career guidance and industry 
            awareness programs, inspiring students to explore opportunities in the fast-growing supply chain industry.
          </p>
        </div>
      </div>

      <div className="mt-16 grid md:grid-cols-2 gap-8">
        <div className="bg-zinc-900 p-8 rounded-2xl border border-zinc-800">
          <h4 className="text-xl font-bold text-white mb-4">The Vision Behind Elites Route</h4>
          <p className="text-zinc-400 leading-relaxed">
            Driven by the vision to bridge the gap between education and employability, Safijo Alphons founded 
            Elites Route Global Talent. The organization focuses on preparing students with practical knowledge, 
            interview readiness, and industry awareness so they can confidently step into logistics careers.
          </p>
        </div>
        
        <div className="bg-zinc-900 p-8 rounded-2xl border border-zinc-800">
          <h4 className="text-xl font-bold text-white mb-4">Continuing the Mission</h4>
          <p className="text-zinc-400 leading-relaxed">
            Today, Safijo Alphons continues to mentor students, collaborate with educational institutions, and 
            support companies looking for skilled logistics talent. Through Elites Route Global Talent, he remains 
            committed to shaping the next generation of logistics professionals.
          </p>
        </div>
      </div>

      <div className="mt-12 bg-gradient-to-r from-amber-500/10 via-amber-500/20 to-amber-500/10 border border-amber-500/30 rounded-2xl p-8 text-center">
        <p className="text-xl text-white italic mb-4">
          "To empower young graduates with the knowledge, skills, and opportunities needed to build successful 
          careers in logistics and supply chain management."
        </p>
        <p className="text-amber-400 font-semibold">— Safijo Alphons, Founder</p>
      </div>
    </div>
  </section>
);

// Services Section
const Services = () => {
  const services = [
    {
      icon: <Briefcase size={40} />,
      title: "Placement Assistance",
      description: "We help you land your first job! End-to-end recruitment support for roles in freight forwarding, shipping, warehouses, and 3PL companies.",
      highlights: ["Documentation & Operations Roles", "Import/Export Coordinator Jobs", "Supply Chain Analyst Positions", "Management Trainee Programs"],
      image: "https://images.unsplash.com/photo-1666558889375-798fa96b559a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjB0ZWFtfGVufDB8fHx8MTc2NjY4NjA5Nnww&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <GraduationCap size={40} />,
      title: "Fresher Programs",
      description: "Just graduated? No worries! Our specialized programs help you transition from campus to your dream corporate job smoothly.",
      highlights: ["Entry-Level Job Placements", "Campus-to-Corporate Support", "Internship Opportunities", "Career Counseling Sessions"],
      image: "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB0cmFpbmluZ3xlbnwwfHx8fDE3NjY2ODYwODl8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <Rocket size={40} />,
      title: "Skill Development",
      description: "Level up your skills with our industry-focused training! Learn from logistics experts and get job-ready certifications.",
      highlights: ["Freight Forwarding Operations", "Customs Documentation", "Warehouse Management", "AI in Supply Chain"],
      image: "https://images.pexels.com/photos/6169164/pexels-photo-6169164.jpeg"
    },
    {
      icon: <Users size={40} />,
      title: "Corporate Training",
      description: "For companies looking to build amazing teams - we provide strategic consulting and customized training solutions.",
      highlights: ["Structured Hiring Strategies", "Employee Retention Programs", "Onboarding & Upskilling", "Future-Ready Team Building"],
      image: "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB0cmFpbmluZ3xlbnwwfHx8fDE3NjY2ODYwODl8MA&ixlib=rb-4.1.0&q=85"
    }
  ];

  return (
    <section id="services" className="py-24 bg-zinc-950" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="services-title">
            What We Do <span className="text-amber-400">For You</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Comprehensive career solutions tailored for students and fresh graduates entering the exciting world of logistics
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div 
              key={index}
              className="bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-800 hover:border-amber-500/50 transition-all group"
              data-testid={`service-card-${index}`}
            >
              <div className="h-48 overflow-hidden">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8">
                <div className="text-amber-400 mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-zinc-400 mb-6">{service.description}</p>
                <div className="space-y-2">
                  {service.highlights.map((highlight, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-zinc-300">
                      <CheckCircle2 className="text-amber-400 flex-shrink-0" size={16} />
                      {highlight}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Career Path Section
const CareerPath = () => {
  const entryRoles = [
    "Warehouse Executive",
    "Logistics Coordinator", 
    "Inventory Controller",
    "Dispatch Executive",
    "Inbound / Outbound Operations Executive",
    "Supply Chain Assistant",
    "E-commerce Warehouse Associate"
  ];

  const midRoles = [
    "Warehouse Supervisor",
    "Operations Executive",
    "Inventory Manager",
    "Supply Chain Analyst",
    "Logistics Operations Manager",
    "Distribution Centre Supervisor"
  ];

  const advancedRoles = [
    "Supply Chain Manager",
    "Warehouse Manager",
    "Logistics Manager",
    "Distribution Head",
    "Supply Chain Consultant"
  ];

  return (
    <section id="career-path" className="py-24 bg-zinc-900" data-testid="career-path-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="career-path-title">
            Career Path <span className="text-amber-400">After Our Training</span>
          </h2>
          <p className="text-zinc-400 max-w-3xl mx-auto text-lg">
            The logistics and supply chain industry is one of the fastest-growing sectors in the world, 
            offering exciting career opportunities for graduates who possess the right practical knowledge and industry skills.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {/* Entry Level */}
          <div className="bg-zinc-800/50 p-8 rounded-2xl border border-zinc-700" data-testid="entry-level-card">
            <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mb-6">
              <GraduationCap className="text-green-400" size={28} />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Entry-Level Roles</h3>
            <p className="text-zinc-500 text-sm mb-6">Fresh graduates begin here</p>
            <ul className="space-y-3">
              {entryRoles.map((role, index) => (
                <li key={index} className="flex items-center gap-2 text-zinc-300 text-sm">
                  <CheckCircle2 className="text-green-400 flex-shrink-0" size={16} />
                  {role}
                </li>
              ))}
            </ul>
          </div>

          {/* Mid Level */}
          <div className="bg-zinc-800/50 p-8 rounded-2xl border border-amber-500/30" data-testid="mid-level-card">
            <div className="w-14 h-14 bg-amber-500/20 rounded-full flex items-center justify-center mb-6">
              <Briefcase className="text-amber-400" size={28} />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Mid-Level Growth</h3>
            <p className="text-zinc-500 text-sm mb-6">2-5 years of experience</p>
            <ul className="space-y-3">
              {midRoles.map((role, index) => (
                <li key={index} className="flex items-center gap-2 text-zinc-300 text-sm">
                  <CheckCircle2 className="text-amber-400 flex-shrink-0" size={16} />
                  {role}
                </li>
              ))}
            </ul>
          </div>

          {/* Advanced */}
          <div className="bg-zinc-800/50 p-8 rounded-2xl border border-zinc-700" data-testid="advanced-level-card">
            <div className="w-14 h-14 bg-purple-500/20 rounded-full flex items-center justify-center mb-6">
              <Rocket className="text-purple-400" size={28} />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Advanced Opportunities</h3>
            <p className="text-zinc-500 text-sm mb-6">Leadership positions</p>
            <ul className="space-y-3">
              {advancedRoles.map((role, index) => (
                <li key={index} className="flex items-center gap-2 text-zinc-300 text-sm">
                  <CheckCircle2 className="text-purple-400 flex-shrink-0" size={16} />
                  {role}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Global Opportunities */}
        <div className="bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-amber-500/10 border border-amber-500/30 rounded-2xl p-8 mb-12">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center">
                <Globe className="text-amber-400" size={40} />
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white mb-4">Global Career Opportunities</h3>
              <p className="text-zinc-400 mb-4">
                The logistics sector is a global industry, which means skilled professionals can explore international opportunities in:
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-zinc-300">
                  <ChevronRight className="text-amber-400" size={16} />
                  E-commerce logistics companies
                </div>
                <div className="flex items-center gap-2 text-zinc-300">
                  <ChevronRight className="text-amber-400" size={16} />
                  International shipping & freight companies
                </div>
                <div className="flex items-center gap-2 text-zinc-300">
                  <ChevronRight className="text-amber-400" size={16} />
                  Global supply chain organizations
                </div>
                <div className="flex items-center gap-2 text-zinc-300">
                  <ChevronRight className="text-amber-400" size={16} />
                  Logistics hubs: Singapore, UAE & more
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h3 className="text-2xl font-bold text-white mb-4">Your Journey Starts Here</h3>
          <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
            The logistics industry offers long-term career growth, global exposure, and leadership opportunities. 
            At Elites Route Global Talent, our goal is to help students take the first confident step into this dynamic industry.
          </p>
          <a 
            href="#contact" 
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-zinc-950 font-semibold rounded-full hover:shadow-lg hover:shadow-amber-500/30 transition-all"
            data-testid="start-career-btn"
          >
            Start Your Career Journey <ChevronRight size={20} />
          </a>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const Testimonials = () => {
  const testimonials = [
    {
      name: "Priya Sharma",
      role: "Documentation Executive",
      company: "DHL Global Forwarding",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face",
      quote: "I was a fresh graduate with zero experience in logistics. Elites Route not only trained me but also placed me at DHL within 3 weeks! The team was super supportive throughout my journey.",
      rating: 5
    },
    {
      name: "Rahul Menon",
      role: "Import Coordinator",
      company: "Kuehne+Nagel",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      quote: "The training program was exactly what I needed. They taught me practical skills that I use every day at work. Highly recommend for anyone wanting to enter the logistics field!",
      rating: 5
    },
    {
      name: "Sneha Reddy",
      role: "Supply Chain Analyst",
      company: "Maersk Line",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      quote: "From college campus to Maersk - it felt like a dream! The team at Elites Route understood my career goals and found the perfect match. Forever grateful!",
      rating: 5
    },
    {
      name: "Arjun Nair",
      role: "Operations Executive",
      company: "DB Schenker",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
      quote: "What I loved most was their honest approach. They didn't just focus on placement - they genuinely cared about my career growth. The guidance was invaluable!",
      rating: 5
    }
  ];

  return (
    <section id="testimonials" className="py-24 bg-zinc-900" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="testimonials-title">
            Success <span className="text-amber-400">Stories</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Don't just take our word for it - hear from students who've launched amazing careers with our help!
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-zinc-800/50 p-8 rounded-2xl border border-zinc-700 hover:border-amber-500/30 transition-all relative"
              data-testid={`testimonial-card-${index}`}
            >
              <Quote className="absolute top-6 right-6 text-amber-500/20" size={48} />
              
              <div className="flex items-center gap-4 mb-6">
                <img 
                  src={testimonial.image} 
                  alt={testimonial.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-amber-500/50"
                />
                <div>
                  <h3 className="text-white font-semibold">{testimonial.name}</h3>
                  <p className="text-amber-400 text-sm">{testimonial.role}</p>
                  <p className="text-zinc-500 text-sm">{testimonial.company}</p>
                </div>
              </div>
              
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="text-amber-400 fill-amber-400" size={16} />
                ))}
              </div>
              
              <p className="text-zinc-300 leading-relaxed italic">"{testimonial.quote}"</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-zinc-400 mb-4">Ready to write your own success story?</p>
          <a 
            href="#contact" 
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-zinc-950 font-semibold rounded-full hover:shadow-lg hover:shadow-amber-500/30 transition-all"
            data-testid="start-journey-btn"
          >
            Start Your Journey <ChevronRight size={20} />
          </a>
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUs = () => {
  const reasons = [
    {
      icon: <Target size={32} />,
      title: "100% Logistics Focus",
      description: "We eat, sleep, and breathe logistics! Our entire focus is on helping you succeed in supply chain careers."
    },
    {
      icon: <GraduationCap size={32} />,
      title: "Fresher-Friendly",
      description: "No experience? No problem! We specialize in helping fresh graduates kickstart their careers."
    },
    {
      icon: <Lightbulb size={32} />,
      title: "Industry Training",
      description: "Get hands-on, practical training designed by logistics experts who know what employers want."
    },
    {
      icon: <Handshake size={32} />,
      title: "Honest & Transparent",
      description: "No hidden fees, no false promises. We're straight-up honest about opportunities and expectations."
    },
    {
      icon: <Rocket size={32} />,
      title: "Quick Placements",
      description: "We work fast to match you with the right companies. Your dream job could be just weeks away!"
    },
    {
      icon: <Users size={32} />,
      title: "Personalized Support",
      description: "Every student is unique. We provide customized guidance based on your skills and career goals."
    }
  ];

  return (
    <section id="why-us" className="py-24 bg-zinc-900" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="why-us-title">
            Why Students <span className="text-amber-400">Love Us</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Here's what makes us your best choice for launching a logistics career
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasons.map((reason, index) => (
            <div 
              key={index}
              className="bg-zinc-800/50 p-8 rounded-xl border border-zinc-700 hover:border-amber-500/50 transition-all group"
              data-testid={`reason-card-${index}`}
            >
              <div className="text-amber-400 mb-4 group-hover:scale-110 transition-transform">{reason.icon}</div>
              <h3 className="text-lg font-bold text-white mb-2">{reason.title}</h3>
              <p className="text-zinc-400 text-sm">{reason.description}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-amber-500/10 via-amber-500/20 to-amber-500/10 border border-amber-500/30 rounded-2xl p-8 max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">Ready to Start Your Journey?</h3>
            <p className="text-zinc-400 mb-6">Join hundreds of students who've launched successful logistics careers with us!</p>
            <a 
              href="#contact" 
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-zinc-950 font-semibold rounded-full hover:shadow-lg hover:shadow-amber-500/30 transition-all"
              data-testid="partner-with-us-btn"
            >
              Let's Connect <ChevronRight size={20} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Industries Section
const Industries = () => {
  const industries = [
    {
      icon: <Ship size={32} />,
      title: "Freight Forwarding & CHA",
      description: "International freight services and customs handling",
      image: "https://images.unsplash.com/photo-1578575437130-527eed3abbec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxmcmVpZ2h0JTIwZm9yd2FyZGluZ3xlbnwwfHx8fDE3NjY2ODYxMjV8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <Ship size={32} />,
      title: "Shipping & Maritime",
      description: "Ocean freight and port logistics operations",
      image: "https://images.unsplash.com/photo-1635851801927-44c4d1c555af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxmcmVpZ2h0JTIwZm9yd2FyZGluZ3xlbnwwfHx8fDE3NjY2ODYxMjV8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <Warehouse size={32} />,
      title: "Warehousing & Cold Chain",
      description: "Storage solutions and temperature logistics",
      image: "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHx3YXJlaG91c2luZ3xlbnwwfHx8fDE3NjY2ODYxMzF8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <Truck size={32} />,
      title: "E-commerce & Last-Mile",
      description: "Fast delivery and distribution networks",
      image: "https://images.pexels.com/photos/4483775/pexels-photo-4483775.jpeg"
    },
    {
      icon: <Package size={32} />,
      title: "FMCG & Manufacturing",
      description: "Consumer goods supply chain management",
      image: "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHx3YXJlaG91c2luZ3xlbnwwfHx8fDE3NjY2ODYxMzF8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: <Pill size={32} />,
      title: "Pharma & Healthcare",
      description: "Medical supplies and vaccine logistics",
      image: "https://images.pexels.com/photos/4483775/pexels-photo-4483775.jpeg"
    }
  ];

  return (
    <section id="industries" className="py-24 bg-zinc-950" data-testid="industries-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="industries-title">
            Industries <span className="text-amber-400">We Cover</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Explore exciting career opportunities across diverse logistics sectors
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {industries.map((industry, index) => (
            <div 
              key={index}
              className="relative h-64 rounded-2xl overflow-hidden group cursor-pointer"
              data-testid={`industry-card-${index}`}
            >
              <img 
                src={industry.image} 
                alt={industry.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="text-amber-400 mb-2">{industry.icon}</div>
                <h3 className="text-lg font-bold text-white mb-1">{industry.title}</h3>
                <p className="text-zinc-400 text-sm">{industry.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Approach Section
const Approach = () => {
  const steps = [
    {
      number: "01",
      title: "Understand",
      description: "We get to know YOU - your skills, interests, and career dreams to find the perfect match."
    },
    {
      number: "02",
      title: "Train",
      description: "Level up with our industry-relevant courses and practical skills that employers actually want."
    },
    {
      number: "03",
      title: "Match",
      description: "We connect you with companies that align with your skills, culture, and career goals."
    },
    {
      number: "04",
      title: "Support",
      description: "Even after placement, we've got your back with ongoing career support and guidance."
    }
  ];

  const partners = [
    "Logistics Companies",
    "HR Leaders",
    "Colleges & Universities",
    "Training Institutes",
    "Fresh Graduates"
  ];

  return (
    <section className="py-24 bg-zinc-900" data-testid="approach-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="approach-title">
            Our <span className="text-amber-400">Simple Process</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            A proven 4-step approach to help you land your dream logistics career
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {steps.map((step, index) => (
            <div 
              key={index}
              className="bg-zinc-800/50 p-8 rounded-xl border border-zinc-700 text-center relative"
              data-testid={`step-card-${index}`}
            >
              <div className="text-5xl font-bold text-amber-500/20 absolute top-4 right-4">{step.number}</div>
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-amber-400">{index + 1}</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
              <p className="text-zinc-400 text-sm">{step.description}</p>
            </div>
          ))}
        </div>

        <div className="bg-zinc-800/30 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-bold text-white mb-6">Who Can Partner With Us?</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {partners.map((partner, index) => (
              <span 
                key={index}
                className="px-6 py-3 bg-zinc-700/50 rounded-full text-zinc-300 text-sm"
                data-testid={`partner-tag-${index}`}
              >
                {partner}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section with WhatsApp
const Contact = () => {
  const whatsappNumber = "918075598840";
  const whatsappMessage = "Hi! I'm interested in learning more about Elites Route Global Talent services.";
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <section id="contact" className="py-24 bg-zinc-950" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="contact-title">
            Let's <span className="text-amber-400">Connect</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Ready to kickstart your logistics career or find amazing talent? Reach out to us!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* WhatsApp CTA */}
          <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 rounded-2xl p-8 border border-green-500/30 flex flex-col justify-center" data-testid="whatsapp-section">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/30">
                <svg viewBox="0 0 24 24" className="w-10 h-10 text-white fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Chat with Us on WhatsApp</h3>
              <p className="text-zinc-400 mb-6">
                Get instant responses! Message us directly on WhatsApp for quick assistance with your career queries.
              </p>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-full transition-all shadow-lg shadow-green-500/30 hover:shadow-green-500/50"
                data-testid="whatsapp-btn"
              >
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Start WhatsApp Chat
              </a>
              <p className="text-zinc-500 text-sm mt-4">+91 8075598840</p>
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <a 
                href="mailto:eliteroutetalent@gmail.com"
                className="bg-zinc-900 p-6 rounded-xl border border-zinc-800 hover:border-amber-500/50 transition-all text-center group"
                data-testid="contact-email"
              >
                <div className="w-14 h-14 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                  <Mail className="text-amber-400" size={24} />
                </div>
                <h3 className="text-white font-semibold mb-1">Email Us</h3>
                <p className="text-zinc-400 text-sm">eliteroutetalent@gmail.com</p>
              </a>

              <a 
                href="tel:+918075598840"
                className="bg-zinc-900 p-6 rounded-xl border border-zinc-800 hover:border-amber-500/50 transition-all text-center group"
                data-testid="contact-phone"
              >
                <div className="w-14 h-14 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                  <Phone className="text-amber-400" size={24} />
                </div>
                <h3 className="text-white font-semibold mb-1">Call Us</h3>
                <p className="text-zinc-400 text-sm">+91-8075598840</p>
              </a>

              <a 
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-zinc-900 p-6 rounded-xl border border-zinc-800 hover:border-green-500/50 transition-all text-center group"
                data-testid="contact-whatsapp"
              >
                <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-green-400 fill-current">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <h3 className="text-white font-semibold mb-1">WhatsApp</h3>
                <p className="text-zinc-400 text-sm">+91-8075598840</p>
              </a>

              <a 
                href="https://maps.google.com/?q=Aspire+Coworks+Koramangala+Bengaluru"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-zinc-900 p-6 rounded-xl border border-zinc-800 hover:border-amber-500/50 transition-all text-center group"
                data-testid="contact-location"
              >
                <div className="w-14 h-14 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                  <MapPin className="text-amber-400" size={24} />
                </div>
                <h3 className="text-white font-semibold mb-1">Visit Us</h3>
                <p className="text-zinc-400 text-sm">Koramangala, Bengaluru</p>
              </a>
            </div>

            <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800" data-testid="address-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Building2 className="text-amber-400" size={24} />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Registered Office</h3>
                  <p className="text-zinc-400 leading-relaxed text-sm">
                    ELITES ROUTE GLOBAL TALENT, Aspire Coworks, No. 472/7 Balaji Arcade, 2nd & 3rd Floor, 
                    A.V.S. Compound, 20th L Cross Road, AVS Layout, Ejipura, Koramangala 4th Block, 
                    Bengaluru, Karnataka – 560095
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="py-12 bg-zinc-900 border-t border-zinc-800" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="Elites Route Global Talent" className="h-20 w-auto" />
        </div>
        
        <p className="text-zinc-500 text-sm text-center">
          Your Gateway to Global Logistics Careers
        </p>
        
        <p className="text-zinc-500 text-sm">
          © 2025 Elites Route Global Talent. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Main App
function App() {
  return (
    <div className="App bg-zinc-950 min-h-screen">
      <Navbar />
      <Hero />
      <About />
      <FounderStory />
      <Services />
      <CareerPath />
      <Contact />
      <Footer />
    </div>
  );
}

export default App;
